import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { EnvelopeOpen, Sparkle, Copy, Trash, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function EmailPage() {
  const [goal, setGoal] = useState("promotion");
  const [audience, setAudience] = useState("");
  const [offer, setOffer] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [subs, setSubs] = useState([]);
  const [newSub, setNewSub] = useState({ email: "", name: "" });

  const loadSubs = useCallback(
    () => api.get("/subscribers")
      .then((r) => setSubs(r.data))
      .catch((err) => console.warn("[email] subscribers load failed:", err?.message || err)),
    [],
  );
  useEffect(() => { loadSubs(); }, [loadSubs]);

  const generate = async () => {
    if (!audience) return toast.error("Audience required");
    setLoading(true);
    try {
      const r = await api.post("/email/campaign", { goal, audience, offer });
      setResult(r.data);
      setChosenSubject(r.data.subject_lines?.[0] || "");
      toast.success("Campaign generated");
    } catch (err) { console.warn("[email] generate failed:", err?.message || err); toast.error("Failed"); }
    finally { setLoading(false); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const addSub = async () => {
    if (!newSub.email) return toast.error("Email required");
    try {
      await api.post("/subscribers", newSub);
      toast.success("Subscriber added"); setNewSub({ email: "", name: "" }); loadSubs();
    } catch (err) { console.warn("[email] addSub failed:", err?.message || err); toast.error("Invalid email"); }
  };
  const delSub = async (id) => { await api.delete(`/subscribers/${id}`); loadSubs(); };

  return (
    <div data-testid="email-page">
      <PageHeader eyebrow="Email Drafting" title="Draft campaigns. Copy. Paste. Send from anywhere." description="Generate full email campaigns with AI — then copy the subject + body into your favourite email tool to actually send." />

      <Tabs defaultValue="compose" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-sm p-1 h-auto">
          <TabsTrigger data-testid="tab-compose" value="compose" className="rounded-sm px-4 py-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white">Compose</TabsTrigger>
          <TabsTrigger data-testid="tab-subscribers" value="subscribers" className="rounded-sm px-4 py-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white">Subscribers ({subs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card className="sharp-card p-5 self-start">
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Campaign type</Label>
                  <Select value={goal} onValueChange={setGoal}>
                    <SelectTrigger data-testid="email-goal" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="promotion">Promotion / offer</SelectItem>
                      <SelectItem value="seasonal">Seasonal reminder</SelectItem>
                      <SelectItem value="reactivation">Re-activation</SelectItem>
                      <SelectItem value="newsletter">Newsletter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Audience</Label><Input data-testid="email-audience" value={audience} onChange={(e)=>setAudience(e.target.value)} placeholder="e.g. past customers in last 12mo" className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Offer</Label><Input data-testid="email-offer" value={offer} onChange={(e)=>setOffer(e.target.value)} placeholder="e.g. 15% off winter prep" className="rounded-sm mt-1" /></div>
                <Button data-testid="btn-gen-email" onClick={generate} disabled={loading} className="w-full bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11"><Sparkle className="mr-2" size={18} />{loading ? "Writing..." : "Generate campaign"}</Button>
              </div>
            </Card>

            <Card className="sharp-card p-5 lg:col-span-2 self-start">
              {!result ? (
                <div className="text-slate-400 text-sm py-10 flex items-center justify-center"><EnvelopeOpen size={20} className="mr-2" />Campaign will appear here</div>
              ) : (
                <div className="space-y-5">
                  {result.subject_lines && (
                    <div>
                      <div className="eyebrow mb-2">A/B subject lines</div>
                      <div className="space-y-1.5">
                        {result.subject_lines.map((s) => (
                          <div key={s} className="flex items-center justify-between p-2 border border-slate-200 rounded-sm">
                            <div className="text-sm text-slate-900">{s}</div>
                            <Button size="sm" variant="outline" onClick={()=>copy(s)} className="rounded-sm"><Copy size={12} /></Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {result.preheader && <div><div className="eyebrow mb-1">Preheader</div><div className="text-sm text-slate-700 italic">{result.preheader}</div></div>}
                  {result.body_markdown && (
                    <div>
                      <div className="flex items-center justify-between mb-2"><div className="eyebrow">Body</div><Button size="sm" variant="outline" className="rounded-sm" onClick={()=>copy(result.body_markdown)}><Copy size={12} className="mr-1" />Copy</Button></div>
                      <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed bg-slate-50 p-4 rounded-sm border border-slate-200">{result.body_markdown}</div>
                    </div>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    {result.cta_primary && <Badge className="rounded-sm bg-navy text-white">CTA: {result.cta_primary}</Badge>}
                    {result.send_time_suggestion && <Badge className="rounded-sm bg-slate-900 text-white">Send: {result.send_time_suggestion}</Badge>}
                  </div>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="subscribers" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card className="sharp-card p-5 self-start">
              <div className="space-y-3">
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Email</Label><Input data-testid="sub-email" type="email" value={newSub.email} onChange={(e)=>setNewSub({...newSub, email: e.target.value})} className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Name (optional)</Label><Input data-testid="sub-name" value={newSub.name} onChange={(e)=>setNewSub({...newSub, name: e.target.value})} className="rounded-sm mt-1" /></div>
                <Button data-testid="btn-add-sub" onClick={addSub} className="w-full bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11"><UserPlus className="mr-2" size={16} />Add subscriber</Button>
              </div>
            </Card>
            <Card className="sharp-card p-5 lg:col-span-2 self-start">
              <div className="eyebrow mb-3">Your list ({subs.length})</div>
              {subs.length === 0 ? <div className="text-sm text-slate-400 py-8 text-center">No subscribers yet</div> : (
                <div className="divide-y divide-slate-100">
                  {subs.map(s => (
                    <div key={s.id} className="flex items-center justify-between py-2.5">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 text-sm truncate">{s.email}</div>
                        {s.name && <div className="text-xs text-slate-500">{s.name}</div>}
                      </div>
                      <button data-testid={`del-sub-${s.id}`} onClick={()=>delSub(s.id)} className="text-slate-400 hover:text-red-500"><Trash size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
